/**
 * "DOWNLOAD ALL" - individual files, no zip.
 *
 * The server hands back a manifest of presigned R2 URLs (see gallery.js for the
 * full reasoning on zip vs. individual files). This module's job is to trigger
 * those downloads without the browser deciding we're a malware page.
 *
 * How it works: for each file we create a hidden <a download href=...> and
 * click it. The presigned URL carries Content-Disposition: attachment, so the
 * browser hands the file to its download manager instead of navigating.
 *
 * JUDGEMENT CALLS - flagging these because they're the fiddly part:
 *
 *  1. CONCURRENCY = 3. Browsers cap parallel connections per host anyway
 *    (~6 in Chrome), and the download manager queues past that. Three keeps a
 *    fast line busy while leaving headroom for the page itself. It is NOT a
 *    real transfer-rate control - once handed to the download manager we lose
 *    visibility, which is the honest limitation of this approach.
 *
 *  2. STAGGER = 250ms between triggers. Chrome shows a "Download multiple
 *    files?" permission prompt on the second download from a site; firing them
 *    in a tight loop makes some browsers silently drop the rest. The stagger
 *    plus the prompt-first-then-continue flow below is what makes this reliable.
 *
 *  3. Failure handling is best-effort by design. Once a download is in the
 *    browser's download manager we get no completion or failure event - the
 *    `download` attribute gives no callback. So this reports "triggered", not
 *    "completed", and the UI says so. The recovery path is the per-photo
 *    download button: anything that failed can be re-fetched individually. The
 *    alternative - fetch() each file into memory to track progress properly -
 *    would pull 20GB through the JS heap. Not worth it.
 *
 *  4. Mobile Safari will not accept a batch of downloads at all; it handles one
 *    at a time and asks each time. The UI detects iOS and recommends a desktop
 *    for the full download rather than pretending it will work.
 */

const CONCURRENCY = 3;
const STAGGER_MS = 250;

export const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export function triggerDownload(url, filename) {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  // Give the browser a beat to pick the click up before tearing the node down.
  setTimeout(() => a.remove(), 1000);
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Triggers every file in the manifest. `onProgress({ triggered, total, file })`
 * fires as each is handed off. Resolves when all have been triggered - not when
 * the browser has finished writing them to disk (see note 3 above).
 */
export async function downloadAll(files, { onProgress, signal } = {}) {
  let triggered = 0;
  let cursor = 0;

  async function worker() {
    while (cursor < files.length) {
      if (signal?.aborted) return;
      const file = files[cursor++];
      triggerDownload(file.url, file.filename);
      triggered += 1;
      onProgress?.({ triggered, total: files.length, file });
      await sleep(STAGGER_MS);
    }
  }

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, files.length || 1) }, worker));
  return triggered;
}
